import { useApolloClient, useLazyQuery } from "@apollo/client/react";
import { useCallback, useState } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import { RunContext } from "components/Run/Context";
import useAnalytics from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import { fetchSanitizedLogs } from "shared/Run/fetchSanitizedLogs";
import { RunState } from "types/generated";
import { downloadFile, downloadFileFromURL } from "utils/file";

import {
  GET_MODULE_LOGS_DOWNLOAD_URL,
  GET_MODULE_LOGS_SANITIZED,
  GET_STACK_LOGS_DOWNLOAD_URL,
  GET_STACK_LOGS_SANITIZED,
} from "./gql";
import { getLogsDownloadUrl, getLogsUnavailableMessage } from "./helpers";

type UseDownloadLogsProps = {
  state: RunState;
  stateVersion?: number | null;
  runId: string;
  stackId: string;
  isModuleRun: boolean;
};

const useDownloadLogs = ({
  state,
  stateVersion,
  runId,
  stackId,
  isModuleRun,
}: UseDownloadLogsProps) => {
  const { reportError } = useTypedContext(FlashContext);
  const {
    run: { state: runState },
  } = useTypedContext(RunContext);
  const trackSegmentAnalyticsEvent = useAnalytics();
  const { stripAnsiCodesFromLogs } = useTypedFlags();
  const client = useApolloClient();
  const [loading, setLoading] = useState(false);

  const [getDownloadUrl] = useLazyQuery(
    isModuleRun ? GET_MODULE_LOGS_DOWNLOAD_URL : GET_STACK_LOGS_DOWNLOAD_URL
  );

  const downloadLogs = useCallback(async () => {
    const fileName = `${stackId}-${runId}-${state}.logs.txt`;

    setLoading(true);

    try {
      if (stripAnsiCodesFromLogs) {
        const logs = await fetchSanitizedLogs(
          client,
          isModuleRun ? GET_MODULE_LOGS_SANITIZED : GET_STACK_LOGS_SANITIZED,
          {
            [isModuleRun ? "moduleId" : "stackId"]: stackId,
            runId,
            state,
            stateVersion,
          }
        );

        if (!logs.exists) {
          reportError({ message: getLogsUnavailableMessage(logs) });
          return;
        }

        downloadFile(fileName, logs.text);
      } else {
        const { data } = await getDownloadUrl({
          variables: {
            [isModuleRun ? "moduleId" : "stackId"]: stackId,
            runId,
            state,
            stateVersion,
          },
        });

        const logsDownloadURL = getLogsDownloadUrl(data);

        if (!logsDownloadURL?.url) {
          reportError({ message: getLogsUnavailableMessage(logsDownloadURL) });
          return;
        }

        downloadFileFromURL(logsDownloadURL.url, fileName);
      }

      trackSegmentAnalyticsEvent("Run Logs - download logs", {
        logsState: state,
        runState,
      });
    } catch {
      reportError({
        title: "Download failed",
        message: "Failed to download logs. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  }, [
    client,
    getDownloadUrl,
    runId,
    state,
    reportError,
    trackSegmentAnalyticsEvent,
    runState,
    stripAnsiCodesFromLogs,
    stackId,
    isModuleRun,
    stateVersion,
  ]);

  return { downloadLogs, loading };
};

export default useDownloadLogs;
